import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Searchable entity picker for the Review Queue "merge into an existing entity" flow.
 *
 * Type 3+ letters → debounced GET /api/review/entities?q=… which prefix-matches
 * dim_entity name AND alias across ALL types (so "ami" finds Amistar, Amistar Top).
 * Picking a result calls onSelect(entity) with {entity_id, entity_name, entity_type,
 * entity_alias}. Reused on both discovered_entity and agent_extraction / entity_mention
 * cards — the parent decides what to do with the pick (fill name+type, or set a
 * merge_target_entity_id).
 *
 * Props:
 *   organization — optional org for cross-org admin routing (passed through to the API)
 *   placeholder  — input placeholder text
 *   onSelect     — (entity) => void, called when a result is chosen
 */
const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

const ps = {
  wrap: { position: 'relative', minWidth: '220px' },
  input: {
    padding: '6px 10px', border: '1px solid #ced4da', borderRadius: '6px',
    fontSize: '13px', minWidth: '220px', width: '100%', boxSizing: 'border-box',
  },
  menu: {
    position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 20,
    background: '#fff', border: '1px solid #ced4da', borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '240px', overflowY: 'auto',
  },
  row: { padding: '7px 10px', cursor: 'pointer', fontSize: '13px', borderTop: '1px solid #f0f0f0' },
  rowFirst: { borderTop: 'none' },
  name: { fontWeight: 600, color: '#1a1a1a' },
  meta: { color: '#6c757d', fontSize: '11px' },
  hint: { padding: '7px 10px', fontSize: '12px', color: '#6c757d' },
};

export default function EntityPicker({ organization, placeholder, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Debounced search. Only fires at >= MIN_CHARS so we don't hammer the API on
  // the first couple of keystrokes (and the server returns [] under 3 anyway).
  useEffect(() => {
    const term = q.trim();
    if (term.length < MIN_CHARS) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const handle = setTimeout(() => {
      const params = { q: term };
      if (organization) params.organization = organization;
      axios.get('/api/review/entities', { params })
        .then(res => { setResults(res.data?.entities || []); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q, organization]);

  // Close the menu on an outside click.
  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const choose = (en) => {
    setQ(en.entity_name);
    setResults([]);
    setOpen(false);
    onSelect(en);
  };

  const term = q.trim();
  return (
    <div ref={wrapRef} style={ps.wrap}>
      <input
        type="text"
        style={ps.input}
        placeholder={placeholder || 'Type 3+ letters to search…'}
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
      />
      {open && (
        <div style={ps.menu}>
          {loading ? (
            <div style={ps.hint}>Searching…</div>
          ) : term.length < MIN_CHARS ? (
            <div style={ps.hint}>Type at least {MIN_CHARS} letters</div>
          ) : results.length === 0 ? (
            <div style={ps.hint}>No matches for “{term}”</div>
          ) : (
            results.map((en, i) => (
              // onMouseDown (not onClick) so the pick registers before the input blurs.
              <div
                key={en.entity_id}
                style={{ ...ps.row, ...(i === 0 ? ps.rowFirst : {}) }}
                onMouseDown={() => choose(en)}
                onMouseEnter={e => { e.currentTarget.style.background = '#f1f6ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={ps.name}>{en.entity_name}</span>
                <span style={ps.meta}> · {en.entity_type}</span>
                {en.entity_alias ? <span style={ps.meta}> · alias: {en.entity_alias}</span> : null}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
